export interface EachThumbnailWithPdfUrl extends EachThumbnail {
  pdfUrl: string;
  isInFileSelected: boolean;
  isInOtherFileSelected: boolean;
}
/**
 * the attachments and the uploading files together
 */
export class MainFiles {
  static apiToMainFilesData(
    attachments: ApiAttachment[],
    uploadingFiles?: UploadingFile[],
  ): UploadingFile[] {
    let data: any[] = attachments || [];
    if (uploadingFiles) {
      const injectedIsRecycled = uploadingFiles.map((f) => ({
        ...f,
        isRecycled: false,
      }));
      data = data.concat(injectedIsRecycled);
    }
    return data.map((d) => {
      return {
        ...d,
        isLoading: d?.status && d.status === 'REQUESTING',
        isSelected: false,
      };
    }) as UploadingFile[];
  }
  static getPartIdToPageNumsMap(
    attachments: ApiAttachment[],
  ): { [partId: string]: number[] } {
    return attachments.reduce(
      (acc, att) => ({
        ...acc,
        [att.partId]: att.pages.map((p) => p.num),
      }),
      {},
    );
  }
  private static _sortByNames(attachments: ApiAttachment[]) {
    return [...attachments].sort((a, b) =>
      (a._filename || a.filename).localeCompare(
        b._filename || b.filename,
      ),
    );
  }
  static getSortedAttachments(
    attachments: ApiAttachment[],
  ): SortedAttachment[] {
    let result: ApiAttachment[] = [];
    const srcToChild = MainFiles.getSrcToChildrenMap(attachments);
    const preSort = MainFiles._sortByNames(attachments);
    console.debug(
      `sortedByNames: `,
      attachments.map((a) => a.filename),
      preSort.map((a) => a.filename),
    );
    if (!srcToChild) {
      result = preSort;
    } else {
      const children = Object.values(srcToChild).reduce(
        (acc, attList) => [...acc, ...attList],
        [],
      );
      const childrenIds = children.map((c) => c.partId);
      // sort alphabetically
      preSort.forEach((att) => {
        // not in src
        if (!(att.partId in srcToChild)) {
          if (!childrenIds.includes(att.partId)) {
            // not in children
            result = result.concat(att);
          }
        } else {
          result = result.concat(att);
          result = result.concat(
            MainFiles._sortByNames(
              srcToChild[att.partId].map((a) => ({
                ...a,
                hasParent: true,
              })),
            ),
          );
        }
      });
    }
    console.debug(`getSortedAttachments results `, result, preSort);
    return result;
  }
  /**
   * .zip to children
   * @param attachments
   */
  static getSrcToChildrenMap(
    attachments: ApiAttachment[],
  ): null | SrcToChildrenMap {
    const allPartIds = attachments.map((att) => att.partId);
    const hasAnyRel = attachments.some(
      (att) =>
        att.fromCompressed &&
        att.compressedSource &&
        allPartIds.includes(att.compressedSource),
    );
    if (!hasAnyRel) return null;
    return attachments.reduce((acc, att) => {
      if (att.fromCompressed && att.compressedSource) {
        const c = att.compressedSource;
        if (!(c in acc)) {
          acc[c] = [];
        }
        acc[c] = acc[c].concat(att);
      }
      return acc;
    }, {});
  }

  /**
   * only .pdf can select page
   * @param ext
   */
  static canSelectPage(ext: string): boolean {
    const result = ext ? ext.toLowerCase() === '.pdf' : false;
    return result;
  }

  static hasAnySelected(files: FormattedMainFile[]) {
    if (!files) return false;
    return files.some((file) => MainFile.isSelected(file));
  }
}

export class MainFile {
  static isATicketFile(file: FormattedMainFile): boolean {
    return file.partId !== undefined;
  }
  static isSelected(file: FormattedMainFile): boolean {
    return file?.isSelected ?? false;
  }
}

interface SrcToChildrenMap {
  [srcPartId: string]: FormattedMainFile[]; // partId[]
}

export interface SortedAttachment extends ApiAttachment {
  hasParent?: boolean;
}

export interface ApiOCR {
  bookingNumbers: string[];
  containerNumbers: string[];
  sealNumbers: string[];
}
export interface ApiAttachment {
  mimeType: string;
  filename: string;
  locked: boolean;
  url: string;
  partId: string;
  pages: EachAttPage[];
  thumbnails: EachThumbnailWithPdfUrl[];
  processed: boolean;
  fromCompressed: boolean;
  compressedSource: string; // partId of the parent
  ext: string;
  driveFileId?: string; // for speradsheet
  _filename: string;
  isRecycled?: boolean;
  ocr: ApiOCR;
}

export interface ApiAttachmentForFS extends ApiAttachment {
  canSelectPage: boolean;
}

export interface FormattedMainFile extends SortedAttachment {
  isLoading: boolean;
  isSelected: boolean;
  uploadingFilesIndex: number;
  error: string;
  isRecycled?: boolean;
  // not UploadingFile will have status
}

export interface UploadingFile extends FormattedMainFile {
  status: 'REQUESTING' | 'ERROR';
}

export interface EachAttPage {
  url: string;
  filename: string;
  mimeType: string;
  num: number;
}
export interface EachThumbnail {
  url: string;
  filename: string;
  mimeType: string;
  num: number;
  pdfUrl: string;
}
